import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Stack,
  IconButton,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VideocamIcon from "@mui/icons-material/VideocamOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { fileUrl } from "../../services/api";

const ext = (name) => (name.split(".").pop() || "").toLowerCase();
const isImage = (n) => ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext(n));
const isVideo = (n) => ["mp4", "webm", "ogg", "mov", "m4v", "avi"].includes(ext(n));
const isPdf = (n) => ext(n) === "pdf";

const tileSx = {
  position: "relative",
  width: 76,
  height: 76,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "2px",
  overflow: "hidden",
  backgroundColor: "background.default",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const removeBtnSx = {
  position: "absolute",
  top: 2,
  right: 2,
  width: 16,
  height: 16,
  p: 0,
  backgroundColor: "rgba(0,0,0,0.55)",
  color: "#fff",
  borderRadius: "2px",
  "&:hover": { backgroundColor: "rgba(187,0,0,0.85)" },
};

const FileTile = ({ name, url, onRemove, dashed }) => {
  const sx = dashed
    ? { ...tileSx, borderStyle: "dashed", borderColor: "primary.main" }
    : tileSx;
  return (
    <Box sx={sx}>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        style={{ display: "flex", width: "100%", height: "100%", alignItems: "center", justifyContent: "center", textDecoration: "none", color: "inherit" }}
      >
        {isImage(name) ? (
          <img
            src={url}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover", opacity: dashed ? 0.85 : 1 }}
          />
        ) : isVideo(name) ? (
          <Stack alignItems="center" spacing={0.25}>
            <VideocamIcon sx={{ fontSize: 22, color: "text.secondary" }} />
            <Typography variant="caption" sx={{ fontSize: "0.6rem" }}>video</Typography>
          </Stack>
        ) : isPdf(name) ? (
          <Stack alignItems="center" spacing={0.25}>
            <PictureAsPdfIcon sx={{ fontSize: 22, color: "#bb0000" }} />
            <Typography variant="caption" sx={{ fontSize: "0.6rem" }}>PDF</Typography>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={0.25}>
            <InsertDriveFileIcon sx={{ fontSize: 22, color: "text.secondary" }} />
            <Typography variant="caption" sx={{ fontSize: "0.6rem", maxWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {name.split(/[\\/]/).pop()}
            </Typography>
          </Stack>
        )}
      </a>
      {onRemove && (
        <Tooltip title="Remove">
          <IconButton size="small" onClick={onRemove} sx={removeBtnSx}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default function FileGallery({
  existing = [],
  newFiles = [],
  removeExisting,
  removeNew,
  addFiles,
  readOnly = false,
  uploading = false,
  folder = "ticket_files",
  emptyText = "No files",
}) {
  const inputRef = useRef(null);
  const previews = useMemo(
    () => newFiles.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [newFiles]
  );

  useEffect(() => {
    return () => previews.forEach((p) => URL.revokeObjectURL(p.url));
  }, [previews]);

  const total = existing.length + newFiles.length;

  return (
    <Box>
      {!readOnly && addFiles && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            Attachments ({total})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            sx={{ fontSize: "0.6875rem", py: 0.25, minHeight: 0 }}
          >
            {uploading ? "Uploading…" : "Add Files"}
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
            onChange={(e) => {
              if (e.target.files?.length) addFiles(Array.from(e.target.files));
              e.target.value = "";
            }}
          />
        </Stack>
      )}
      {total === 0 ? (
        <Typography variant="caption" color="text.disabled">
          {emptyText}
        </Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {existing.map((name) => (
            <FileTile
              key={name}
              name={name}
              url={fileUrl(folder, name)}
              onRemove={!readOnly && removeExisting ? () => removeExisting(name) : null}
            />
          ))}
          {previews.map((p, i) => (
            <FileTile
              key={p.url}
              name={p.file.name}
              url={p.url}
              dashed
              onRemove={!readOnly && removeNew ? () => removeNew(i) : null}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
